import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CLOUD_FUNCTIONS } from '../../auth/FirebaseContext';
import { useAuthContext } from '../../auth/useAuthContext';
import { brandConfig } from '../../config';
import { cloud_mintMessagingIdentityToken } from '../../utils/mrr/cloudFunctions';
import { hasSessionAdmin } from '../../utils/mrr/emulator/signInAsOtherUser';
import { Roles } from '../../utils/mrr/userConstants';
import {
  buildInitScript,
  defaultBootstrapScriptSrc,
  isSalesforceEmbeddingConfigured,
  MintMessagingIdentityTokenResponse,
  setMessagingIdentityToken,
} from './embeddedMessagingSession';
import { injectEmbeddedMessagingStyles } from './embeddedMessagingStyles';
import {
  cleanupEmbeddedChat,
  waitForEmbeddedChatTeardown,
} from './salesforceEmbeddedMessagingTeardown';

const INIT_SCRIPT_ID = 'salesforce-embedded-init-global';
const BOOTSTRAP_SCRIPT_ID = 'salesforce-embedded-bootstrap-global';

type EmbeddedChatProviderProps = {
  children: React.ReactNode;
};

/**
 * Provider that manages the Salesforce Embedded Chat lifecycle.
 * Mounted once at the app level (see App), so a single widget instance persists across all pages —
 * client-side navigation never unmounts the provider, so the widget is bootstrapped exactly once
 * per session and never redraws on route changes. Initializes once the guest is authenticated and
 * their UUID is available: mints a User Verification JWT and calls setIdentityToken. Omni-Channel
 * stamps the Messaging End User from MessagingPlatformKey (`uid:{uuid}`); the portal does not send
 * hidden pre-chat. Guest end-chat ends the MessagingSession only — Conversation continuity stays
 * with the SDK. Teardown runs on logout (FirebaseContext).
 *
 * ============================== CHAT AND SESSION CHANGES ==============================
 * The widget carries a verified Salesforce identity for one guest, so it must never outlive
 * a change of signed-in user:
 *   - Chat only runs for a genuine guest session: role `user`, and NOT while a super-user is
 *     signed in as that guest (admin mode). Admin, developer, and support accounts get no chat.
 *     This keeps super-users from viewing a guest's conversations, or messaging agents as the
 *     guest, while impersonating.
 *   - Whenever the chat identity changes (guest -> nobody, guest -> another guest, guest ->
 *     admin mode), any live widget is torn down before a new one may bootstrap.
 *   - Logout still tears down via FirebaseContext first; the identity watch below then finds
 *     nothing left to remove.
 * ======================================================================================
 */
export function EmbeddedChatProvider({ children }: EmbeddedChatProviderProps) {
  const { isAuthenticated, user, role } = useAuthContext();
  const messaging = brandConfig.salesforceEmbeddedMessaging;

  const initializationAttemptedRef = useRef(false);
  const activeChatIdentityRef = useRef('');

  const ready = useMemo(() => isSalesforceEmbeddingConfigured(messaging), [messaging]);

  const guestUuid = user?.uuid?.trim() ?? '';

  const isGenuineGuestSession = role === Roles.User && !hasSessionAdmin();

  const shouldInitialize = Boolean(ready && isAuthenticated && guestUuid && isGenuineGuestSession);

  const chatIdentity = shouldInitialize ? guestUuid : '';

  // Must stay above the bootstrap effect: a teardown started here is awaited by the bootstrap
  // in the same commit.
  useEffect(() => {
    const previousIdentity = activeChatIdentityRef.current;
    activeChatIdentityRef.current = chatIdentity;
    if (previousIdentity === chatIdentity) {
      return;
    }
    initializationAttemptedRef.current = false;
    if (window.__embeddedChatInitialized || document.getElementById(BOOTSTRAP_SCRIPT_ID)) {
      cleanupEmbeddedChat();
    }
  }, [chatIdentity]);

  const mintAndSetIdentityToken = useCallback(async (): Promise<boolean> => {
    try {
      const request = httpsCallable<
        Record<string, never>,
        MintMessagingIdentityTokenResponse
      >(CLOUD_FUNCTIONS, cloud_mintMessagingIdentityToken);
      const { data } = await request({});
      if (!data?.identityToken) {
        console.error('[EmbeddedChatProvider] mintMessagingIdentityToken returned no token');
        return false;
      }
      return await setMessagingIdentityToken(data.identityToken);
    } catch (err) {
      console.error('[EmbeddedChatProvider] Failed to mint/set identity token', err);
      return false;
    }
  }, []);

  // Bootstrap the widget once the guest UUID is available.
  useEffect(() => {
    if (!shouldInitialize) {
      return undefined;
    }

    let cancelled = false;

    const bootstrapEmbeddedChat = async () => {
      await waitForEmbeddedChatTeardown();
      if (cancelled) {
        return;
      }

      if (window.__embeddedChatInitialized) {
        return;
      }

      if (initializationAttemptedRef.current) {
        return;
      }

      initializationAttemptedRef.current = true;

      document.getElementById(INIT_SCRIPT_ID)?.remove();
      document.getElementById(BOOTSTRAP_SCRIPT_ID)?.remove();

      const bootstrapSrc = defaultBootstrapScriptSrc(messaging.siteUrl, Date.now());

      // Injected before the SDK renders so the widget never paints under the app header.
      injectEmbeddedMessagingStyles();

      const initEl = document.createElement('script');
      initEl.id = INIT_SCRIPT_ID;
      initEl.textContent = buildInitScript(messaging);
      document.body.appendChild(initEl);

      const bootstrapEl = document.createElement('script');
      bootstrapEl.id = BOOTSTRAP_SCRIPT_ID;
      bootstrapEl.src = bootstrapSrc;
      bootstrapEl.async = true;
      bootstrapEl.onload = () => {
        if (cancelled) {
          return;
        }
        window.initEmbeddedMessaging?.();
        window.__embeddedChatInitialized = true;
      };
      bootstrapEl.onerror = () => {
        initializationAttemptedRef.current = false;
        console.error(
          '[EmbeddedChatProvider] Failed to load Salesforce messaging bootstrap from',
          bootstrapSrc
        );
      };
      document.body.appendChild(bootstrapEl);
    };

    bootstrapEmbeddedChat();

    return () => {
      cancelled = true;
      if (!window.__embeddedChatInitialized) {
        initializationAttemptedRef.current = false;
      }
    };
  }, [shouldInitialize, messaging]);

  // The provider lives at the app root, so unmount only happens when the whole app is torn down
  // (or in dev hot-reload). The Salesforce SDK's injected widget/globals aren't part of React's
  // tree, so remove them here.
  useEffect(() => {
    return () => {
      cleanupEmbeddedChat();
      initializationAttemptedRef.current = false;
    };
  }, []);

  // On ready: mint JWT and setIdentityToken. The SDK fires ready again after clearSession() on
  // logout/re-init, so the listener stays registered and re-applies the verified session.
  useEffect(() => {
    if (!shouldInitialize) {
      return undefined;
    }

    const apply = () => {
      mintAndSetIdentityToken().catch((err) => {
        console.error('[EmbeddedChatProvider] Failed to apply verified session', err);
      });
    };

    if (window.__embeddedMessagingReady) {
      apply();
    }

    window.addEventListener('embeddedMessagingReadyForPrechat', apply);
    return () => {
      window.removeEventListener('embeddedMessagingReadyForPrechat', apply);
    };
  }, [shouldInitialize, mintAndSetIdentityToken]);

  // Refresh identity token within 30s of expiry so the verified session stays active.
  useEffect(() => {
    if (!shouldInitialize) {
      return undefined;
    }

    const handleTokenExpired = () => {
      mintAndSetIdentityToken().catch((err) => {
        console.error('[EmbeddedChatProvider] Failed to refresh identity token', err);
      });
    };

    window.addEventListener('onEmbeddedMessagingIdentityTokenExpired', handleTokenExpired);
    return () => {
      window.removeEventListener('onEmbeddedMessagingIdentityTokenExpired', handleTokenExpired);
    };
  }, [shouldInitialize, mintAndSetIdentityToken]);

  return <>{children}</>;
}

export default EmbeddedChatProvider;
